// src/components/admin/scheduling/components/SchedulingSettingsHub.js
//
// Scheduling "Settings" hub — a clean, card-based menu that replaces the old
// tabbed settings modal. Each card (icon + title + description) opens a focused
// management surface.

import React from "react";
import { CalendarDays, ListOrdered, ChevronRight, X } from "lucide-react";

function HubCard({ icon: Icon, iconBg, iconColor, title, description, onClick }) {
  const [hover, setHover] = React.useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...S.card,
        borderColor: hover ? "#c7d2fe" : "#e5e7eb",
        boxShadow: hover ? "0 8px 24px rgba(15,23,42,0.10)" : "0 1px 2px rgba(15,23,42,0.04)",
        transform: hover ? "translateY(-2px)" : "none",
      }}
    >
      <div style={{ ...S.cardIcon, background: iconBg }}>
        <Icon size={22} color={iconColor} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={S.cardTitle}>{title}</div>
        <div style={S.cardDesc}>{description}</div>
      </div>
      <ChevronRight size={18} color={hover ? "#4f46e5" : "#9ca3af"} style={{ flexShrink: 0, alignSelf: "center" }} />
    </button>
  );
}

export default function SchedulingSettingsHub({
  open,
  onClose,
  onOpenScheduleCalendar,
  onOpenTutorOrder,
}) {
  if (!open) return null;

  return (
    <div style={S.overlay}>
      <div style={S.header}>
        <button style={S.iconBtn} onClick={onClose} aria-label="Close">
          <X size={16} color="#374151" />
        </button>
        <div>
          <div style={S.title}>Settings</div>
          <div style={S.subtitle}>Manage scheduling preferences</div>
        </div>
      </div>

      <div style={S.scroll}>
        <div style={S.container}>
          <div style={S.grid}>
            <HubCard
              icon={CalendarDays}
              iconBg="#eef2ff"
              iconColor="#4f46e5"
              title="Schedule Calendar"
              description="Set open/closed days, start & end times, and the default weekly schedule."
              onClick={onOpenScheduleCalendar}
            />
            <HubCard
              icon={ListOrdered}
              iconBg="#f0fdf4"
              iconColor="#16a34a"
              title="Tutor Display Order"
              description="Reorder how tutors appear across the schedule."
              onClick={onOpenTutorOrder}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "#fff",
    zIndex: 1100,
    display: "flex",
    flexDirection: "column",
  },
  header: {
    flexShrink: 0,
    borderBottom: "1px solid #e5e7eb",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  title: { fontSize: 18, fontWeight: 800, color: "#111827", letterSpacing: "-0.4px", lineHeight: 1.1 },
  subtitle: { fontSize: 11.5, color: "#9ca3af", marginTop: 3 },
  iconBtn: {
    width: 32,
    height: 32,
    borderRadius: 9,
    border: "1px solid #e5e7eb",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },
  scroll: { flex: 1, overflowY: "auto" },
  container: {
    maxWidth: "var(--cal-page-max-width, 1440px)",
    width: "100%",
    margin: "0 auto",
    padding: "28px 24px 60px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 16,
    maxWidth: 760,
  },
  card: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    textAlign: "left",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: "18px 18px",
    cursor: "pointer",
    transition: "box-shadow 0.15s, transform 0.15s, border-color 0.15s",
    width: "100%",
  },
  cardIcon: {
    width: 44,
    height: 44,
    borderRadius: 11,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  cardTitle: { fontSize: 15, fontWeight: 700, color: "#111827", letterSpacing: "-0.2px" },
  cardDesc: { fontSize: 12.5, color: "#6b7280", marginTop: 4, lineHeight: 1.45 },
};
